import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Puzzle, Mountain, Plus, Search, X, Flag, CheckCircle2, Activity, Target } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  todo: "bg-slate-500/15 text-slate-300",
  in_progress: "bg-amber-500/15 text-amber-400",
  code_review: "bg-blue-500/15 text-blue-400",
  testing: "bg-violet-500/15 text-violet-400",
  completed: "bg-emerald-500/15 text-emerald-400",
};
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function TaskPicker({ open, onOpenChange, projectId, excludeIds, title, onPick }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open || !projectId) return;
    api.get(`/tasks?project_id=${projectId}`).then((r) => setAll(r.data));
    setQ("");
  }, [open, projectId]);
  const filtered = useMemo(() => all.filter((t) => {
    if (excludeIds.includes(t.id)) return false;
    if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }).slice(0, 100), [all, excludeIds, q]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="task-picker">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Pick an existing task from this project to attach.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="task-picker-search" />
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1 mt-2">
          {filtered.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No matching unattached tasks.</div>}
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => { onPick(t); onOpenChange(false); }}
              className="w-full glass rounded-md px-3 py-2 flex items-center gap-2 hover:border-primary/40 text-left"
              data-testid={`pick-task-${t.id}`}
            >
              <Badge className={`${STATUS_COLOR[t.status] || "bg-muted"} text-[9px] shrink-0 capitalize border-0`}>{t.status.replace("_", " ")}</Badge>
              <span className="flex-1 min-w-0 text-sm truncate">{t.title}</span>
              <Badge variant="outline" className="text-[10px] capitalize shrink-0">{t.priority}</Badge>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FeatureDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [feature, setFeature] = useState(null);
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api.get(`/features/${id}`).then((r) => setFeature(r.data));

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
  }, [id]);

  useEffect(() => {
    if (feature?.project_id) {
      api.get(`/projects/${feature.project_id}`).then((r) => setProject(r.data)).catch(() => {});
    }
  }, [feature?.project_id]);

  if (!feature) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const canWrite = user?.role === "admin" || project?.manager_id === user?.id || (user?.role === "branch_admin" && project?.created_by === user?.id);
  const owner = employees.find((e) => e.id === feature.owner_id);

  const attachTask = async (task) => {
    try { await api.post(`/features/${id}/tasks/${task.id}`); toast.success("Task attached"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const detachTask = async (tid) => {
    if (!window.confirm("Remove this task from the feature? (It stays in the project)")) return;
    try { await api.delete(`/features/${id}/tasks/${tid}`); toast.success("Task detached"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const kpis = [
    { label: "Progress %",  value: `${feature.progress || 0}%`,         icon: Target,       color: "text-primary" },
    { label: "Total Tasks", value: feature.task_count || 0,              icon: Activity,     color: "text-amber-500" },
    { label: "Done",        value: feature.completed_task_count || 0,    icon: CheckCircle2, color: "text-emerald-500" },
    { label: "Priority",    value: feature.priority || "medium",         icon: Flag,         color: "text-violet-500" },
  ];

  return (
    <div className="space-y-6 max-w-6xl">
      <Button data-testid="feat-back" variant="ghost" size="sm" onClick={() => nav("/features")}><ArrowLeft className="w-4 h-4 mr-1" />Back to features</Button>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Puzzle className="w-3.5 h-3.5" /> {feature.identifier} · {project?.name || "—"}
            </div>
            <h1 data-testid="feat-title" className="font-display font-semibold text-3xl tracking-tighter mt-1">{feature.name}</h1>
            {feature.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{feature.description}</p>}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge className="capitalize">{(feature.status || "backlog").replace("_", " ")}</Badge>
              <Badge variant="outline" className="capitalize">{feature.priority}</Badge>
              {feature.epic && (
                <Link to={`/epics/${feature.epic.id}`}>
                  <Badge variant="outline" className="gap-1 hover:border-primary/40">
                    <Mountain className="w-3 h-3" />{feature.epic.identifier} · {feature.epic.name}
                  </Badge>
                </Link>
              )}
              {feature.start_date && <Badge variant="outline" className="gap-1"><Flag className="w-3 h-3" />Start {feature.start_date}</Badge>}
              {feature.due_date && <Badge variant="outline" className="gap-1"><Flag className="w-3 h-3" />Due {feature.due_date}</Badge>}
            </div>
          </div>
          {owner && (
            <div className="flex items-center gap-2 text-sm">
              <div className="text-[10px] uppercase text-muted-foreground">Owner</div>
              <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>
              <span>{owner.full_name}</span>
            </div>
          )}
        </div>
        <div className="mt-5">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${feature.progress || 0}%` }} /></div>
            <span className="text-sm tabular-nums font-medium">{feature.progress || 0}%</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">{feature.completed_task_count || 0} of {feature.task_count || 0} tasks completed</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="glass rounded-xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-background/50 flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="number font-medium text-xl leading-none mt-1 capitalize">{value}</div></div>
          </div>
        ))}
      </div>

      <SectionCard
        title={`Tasks (${(feature.tasks || []).length})`}
        subtitle="Tasks that make up this feature"
        actions={canWrite && (
          <Button size="sm" data-testid="feat-attach-task-btn" onClick={() => setPickerOpen(true)}><Plus className="w-4 h-4 mr-1" />Attach task</Button>
        )}
      >
        {(feature.tasks || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No tasks yet. Attach existing tasks or edit a task and set this feature as its parent.</div>
        ) : (
          <div className="space-y-1.5" data-testid="feat-tasks">
            {feature.tasks.map((t) => (
              <div key={t.id} className="glass rounded-md px-3 py-2 flex items-center gap-2" data-testid={`feat-task-${t.id}`}>
                <Badge className={`${STATUS_COLOR[t.status] || "bg-muted"} text-[9px] capitalize border-0 shrink-0`}>{t.status.replace("_", " ")}</Badge>
                <Link to={`/tasks/${t.id}`} className="flex-1 min-w-0 text-sm hover:text-primary truncate">{t.title}</Link>
                <Badge variant="outline" className="text-[10px] capitalize shrink-0">{t.priority}</Badge>
                {t.estimated_hours > 0 && <span className="text-[10px] text-muted-foreground number shrink-0">{t.estimated_hours}h</span>}
                {t.due_date && <Badge variant="outline" className="text-[10px] gap-1 shrink-0"><Flag className="w-2.5 h-2.5" />{t.due_date}</Badge>}
                {canWrite && (
                  <Button size="icon" variant="ghost" data-testid={`feat-detach-${t.id}`} onClick={() => detachTask(t.id)} className="h-7 w-7"><X className="w-3.5 h-3.5" /></Button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <TaskPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        projectId={feature.project_id}
        excludeIds={(feature.tasks || []).map((t) => t.id)}
        title={`Attach task to ${feature.identifier}`}
        onPick={attachTask}
      />
    </div>
  );
}
